import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { trackApiResponseTime, trackApiError } from '../../utils/metrics';

type JsonPrimitive = string | number | boolean | null | undefined;
type JsonObject = { [key: string]: JsonValue };
type JsonArray = JsonValue[];
type JsonValue = JsonPrimitive | JsonObject | JsonArray;

interface RequestMetadata {
  startTime: number;
  span: Sentry.Span;
}

const requestMetadataMap = new WeakMap<AxiosRequestConfig, RequestMetadata>();

const SENSITIVE_HEADERS = ['authorization', 'cookie', 'st-auth-mode'];
const SENSITIVE_PARAMS = ['token', 'password', 'secret'];
const SENSITIVE_PATTERNS = [
  /token$/i,
  /password$/i,
  /secret$/i,
  /_key$/i,
  /^api_?key$/i,
  /^auth_?token$/i,
  /^access_?token$/i,
  /^refresh_?token$/i,
  /^session_?id$/i,
  /^session_?token$/i,
  /jwt$/i,
  /bearer$/i,
  /credentials?$/i,
];

function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();

  if (SENSITIVE_HEADERS.includes(lowerKey) || SENSITIVE_PARAMS.includes(lowerKey)) {
    return true;
  }

  return SENSITIVE_PATTERNS.some((pattern) => pattern.test(lowerKey));
}

function sanitizeData(data: JsonValue): JsonValue {
  if (!data) return data;

  if (Array.isArray(data)) {
    return data.map((item) => sanitizeData(item));
  }

  if (typeof data === 'object') {
    const sanitized: JsonObject = {};

    for (const key in data) {
      if (isSensitiveKey(key)) {
        sanitized[key] = '[Redacted]';
      } else {
        sanitized[key] = sanitizeData(data[key]);
      }
    }

    return sanitized;
  }

  return data;
}

function safeStringify(data: JsonValue): string {
  try {
    return JSON.stringify(sanitizeData(data));
  } catch {
    return '[Non-serializable data]';
  }
}

function getFullUrl(config: AxiosRequestConfig): string {
  return `${config.baseURL || ''}${config.url || ''}`;
}

function getRequestDescription(config: AxiosRequestConfig): string {
  const method = config.method?.toUpperCase() || 'GET';
  const url = getFullUrl(config);
  return `${method} ${url}`;
}

function formatDataForBreadcrumb(data: JsonValue): JsonValue {
  if (!data) return undefined;

  const stringified = safeStringify(data);
  if (stringified.length < 1000) {
    return sanitizeData(data);
  }

  return '[Body too large]';
}

export const SentryInterceptor = {
  addSentryInterceptors(axiosInstance: AxiosInstance): void {
    axiosInstance.interceptors.request.use(
      (config) => {
        const span = Sentry.startInactiveSpan({
          op: 'http.client',
          name: getRequestDescription(config),
        });

        if (span) {
          span.setAttributes({
            'http.method': config.method?.toUpperCase(),
            'http.url': getFullUrl(config),
            'http.query': config.params ? safeStringify(config.params as JsonValue) : undefined,
          });

          requestMetadataMap.set(config, {
            startTime: Date.now(),
            span,
          });
        }

        Sentry.addBreadcrumb({
          category: 'http.request',
          type: 'http',
          level: 'info',
          data: {
            method: config.method?.toUpperCase(),
            url: getFullUrl(config),
            headers: sanitizeData(config.headers as JsonValue),
            params: sanitizeData(config.params as JsonValue),
            data: formatDataForBreadcrumb(config.data as JsonValue),
          },
        });

        return config;
      },
      (error) => {
        Sentry.captureException(error);
        return Promise.reject(error);
      }
    );

    axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const metadata = requestMetadataMap.get(response.config);

        if (metadata && metadata.span) {
          metadata.span.setStatus({ code: 1 });
          metadata.span.setAttributes({
            'http.status_code': response.status,
            'http.response_content_length': response.headers['content-length'],
          });
          metadata.span.end();

          requestMetadataMap.delete(response.config);
        }

        const duration = metadata ? Date.now() - metadata.startTime : undefined;

        if (duration !== undefined) {
          const url = response.config.url || '';
          const method = response.config.method?.toUpperCase() || 'GET';
          trackApiResponseTime(url, method, duration, response.status);
        }

        Sentry.addBreadcrumb({
          category: 'http.response',
          type: 'http',
          level: 'info',
          data: {
            method: response.config.method?.toUpperCase(),
            url: getFullUrl(response.config),
            status: response.status,
            duration,
            data: formatDataForBreadcrumb(response.data as JsonValue),
          },
        });

        return response;
      },
      (error: AxiosError) => {
        const metadata = error.config ? requestMetadataMap.get(error.config) : null;

        if (metadata && metadata.span && error.config) {
          metadata.span.setStatus({
            code: 2,
            message: error.message,
          });
          metadata.span.setAttributes({
            'http.status_code': error.response?.status,
            'error.type': error.name,
          });
          metadata.span.end();

          requestMetadataMap.delete(error.config);
        }

        Sentry.addBreadcrumb({
          category: 'http.error',
          type: 'http',
          level: 'error',
          data: {
            method: error.config?.method?.toUpperCase(),
            url: error.config ? getFullUrl(error.config) : 'unknown',
            status: error.response?.status,
            statusText: error.response?.statusText,
            duration: metadata ? Date.now() - metadata.startTime : undefined,
            errorMessage: error.message,
            errorData: error.response?.data ? sanitizeData(error.response.data as JsonValue) : undefined,
          },
        });

        if (error.response?.status) {
          const url = error.config?.url || '';
          const method = error.config?.method?.toUpperCase() || 'GET';
          trackApiError(url, method, error.response.status);
        }

        if (error.response?.status && error.response.status >= 500) {
          Sentry.captureException(error, {
            tags: {
              'http.status_code': error.response.status,
              'http.method': error.config?.method?.toUpperCase(),
            },
            extra: {
              url: error.config ? getFullUrl(error.config) : 'unknown',
              response: sanitizeData(error.response?.data as JsonValue),
            },
          });
        }

        return Promise.reject(error);
      }
    );
  },
};

export default SentryInterceptor;
